import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { date as fmtDate } from "@/lib/format";
import { Button, PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { popup } from "@/components/popup";

interface PendingItem {
  id: number;
  user_id: number;
  telegram_id: number;
  username: string | null;
  reason: string;
  plan: string;
  days: number;
  hours: number;
  attempts: number;
  last_error: string | null;
  created_at: string | null;
  last_attempt_at: string | null;
}

interface PendingResponse {
  summary: {
    pending: number;
    users: number;
    days: number;
    by_reason: Record<string, number>;
    by_error: Record<string, number>;
  };
  items: PendingItem[];
}

const REASON_LABEL: Record<string, string> = {
  invite_reward: "за друга",
  trial: "тест",
  paid: "оплата",
  admin: "вручную",
};

const ERROR_LABEL: Record<string, string> = {
  no_endpoint: "нет свободного хоста",
  unknown: "причина неизвестна",
};

export default function PendingGrants() {
  const qc = useQueryClient();
  const q = useQuery<PendingResponse>({
    queryKey: ["pending-grants"],
    queryFn: () => api("/api/admin/pending-grants?limit=200"),
  });

  const retry = useMutation<{ tried: number; done: number }>({
    mutationFn: () =>
      api("/api/admin/pending-grants/retry?limit=200", { method: "POST" }) as Promise<{
        tried: number;
        done: number;
      }>,
    onSuccess: (r) => {
      popup({
        tone: r.done ? "success" : "info",
        title: "Выдача",
        body: `Попыток: ${r.tried} · выдано: ${r.done}`,
      });
      qc.invalidateQueries({ queryKey: ["pending-grants"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) =>
      popup({ tone: "error", title: "Выдача", body: (e as Error).message }),
  });

  const cancel = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/pending-grants/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending-grants"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const s = q.data?.summary;
  const items = q.data?.items ?? [];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Ожидают выдачи"
        right={
          <Button onClick={() => retry.mutate()} disabled={retry.isPending || !s?.pending}>
            {retry.isPending ? "Выдаю…" : "Выдать сейчас"}
          </Button>
        }
      />

      <p className="text-zinc-500 text-sm mb-6 max-w-2xl">
        Начисления, которые не удалось выдать — обычно из-за отсутствия свободного
        хоста. Планировщик повторяет попытку автоматически; юзеру уходит сообщение
        только после успешной выдачи.
      </p>

      {s && (
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card label="Записей" value={s.pending} />
          <Card label="Юзеров" value={s.users} />
          <Card label="Дней" value={s.days} />
          <Card
            label="Причина"
            value={
              Object.entries(s.by_error)
                .map(([e, n]) => `${ERROR_LABEL[e] ?? e}: ${n}`)
                .join(" · ") || "—"
            }
            small
          />
        </section>
      )}

      <Table>
        <thead>
          <tr>
            <Th>Юзер</Th>
            <Th>Начислено</Th>
            <Th>Основание</Th>
            <Th>Причина задержки</Th>
            <Th>Попыток</Th>
            <Th>Создано</Th>
            <Th>{""}</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id}>
              <Td>
                <div className="text-zinc-200">
                  {it.username ? `@${it.username}` : `id ${it.user_id}`}
                </div>
                <div className="text-zinc-500 text-xs tabular-nums">{it.telegram_id}</div>
              </Td>
              <Td>
                <span className="tabular-nums">
                  {it.days ? `${it.days} дн.` : it.hours ? `${it.hours} ч.` : "—"}
                </span>
              </Td>
              <Td>
                <Pill>{REASON_LABEL[it.reason] ?? it.reason}</Pill>
              </Td>
              <Td className="text-zinc-400">
                {it.last_error ? (ERROR_LABEL[it.last_error] ?? it.last_error) : "—"}
              </Td>
              <Td className="tabular-nums text-zinc-400">{it.attempts}</Td>
              <Td className="text-zinc-500">{it.created_at ? fmtDate(it.created_at) : "—"}</Td>
              <Td>
                <button
                  className="text-zinc-500 hover:text-rose-300 text-sm"
                  onClick={() => cancel.mutate(it.id)}
                >
                  Снять
                </button>
              </Td>
            </tr>
          ))}
          {!items.length && (
            <tr>
              <td colSpan={7} className="text-zinc-500 py-8 text-center">
                Ничего не ждёт выдачи.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function Card({
  label,
  value,
  small,
}: {
  label: string;
  value: string | number;
  small?: boolean;
}) {
  return (
    <div className="rounded-2xl card-elev p-5">
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">{label}</div>
      <div
        className={`${small ? "text-sm text-zinc-300" : "text-3xl font-bold tabular-nums"} tracking-tight`}
      >
        {value}
      </div>
    </div>
  );
}
